import moment from 'moment';

import type {AdminSchool} from './useAdminSchools';
import {
  restrictionRemovalMessage,
  restrictionRemovalOverdueMessage
} from './manageRestrictionsModal.constants';

/**
 * When the school's restrictions are scheduled for removal — `AdminSchool.restrictionRemovalDate`,
 * the end date of the school's running `contentLockGracePeriods` row (#20975), stamped by
 * `useAdminSchools`. Absent (null here) when the school isn't in a grace window, which hides
 * every grace-window surface (header note, breakdown tooltip, row warning + locked toggle).
 */
export function getRestrictionRemovalDate(school: AdminSchool): moment.Moment | null {
  // The backend row is the authority — no hasPro second-guessing here. The license's stored
  // `active` flag can lag the grace observation (a window opened the day before expiry), and the
  // backend already terminates the window when pro is restored.
  return school.restrictionRemovalDate ? moment(school.restrictionRemovalDate) : null;
}

/**
 * A removal is on the books for this school's restrictions on this content — whether or not the
 * date has come round yet.
 *
 * Every warning surface keys on this rather than on the date still being ahead, so that it agrees
 * with the locked toggle: the toggle keys on the row existing, and the backend's `:open` read
 * serves rows whose `end_date` has already passed (it filters on `terminated_at` alone).
 */
export function hasScheduledRestrictionRemoval(
  school: AdminSchool,
  isRestricted: boolean
): boolean {
  return isRestricted && getRestrictionRemovalDate(school) !== null;
}

/** "Aug 12th, 2026" — the removal-date wording the modal's warning lines use. */
export function formatRemovalDate(removalDate: moment.Moment): string {
  return removalDate.format('MMM Do, YYYY');
}

/** The warning line for a scheduled removal, tensed by whether its date has already passed. */
export function removalWarningMessage(
  removalDate: moment.Moment,
  now: moment.Moment = moment()
): string {
  const formatted = formatRemovalDate(removalDate);
  return removalDate.isAfter(now)
    ? restrictionRemovalMessage(formatted)
    : restrictionRemovalOverdueMessage(formatted);
}
